/**
 * Build Info Component
 *
 * Purpose: Displays build information (date, commit hash, version) in the sidebar footer
 *          to help identify which version of the application is currently deployed
 *
 * Architecture: Simple component that reads environment variables and displays build info
 *
 * Dependencies: React, Material-UI
 * Author: Development Team
 * Created: 2025-01-27
 */

import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { Info } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components - Updated for dark sidebar
const BuildInfoContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  backgroundColor: 'rgba(0, 0, 0, 0.15)',
  fontSize: '0.75rem',
  color: 'rgba(255, 255, 255, 0.6)',
  textAlign: 'center',
  userSelect: 'none',
}));

const BuildInfoText = styled(Typography)(() => ({
  fontSize: '0.7rem',
  lineHeight: 1.2,
  fontFamily: 'monospace',
  color: 'rgba(255, 255, 255, 0.6)',
}));

const BuildInfoIcon = styled(Info)(({ theme }) => ({
  fontSize: '0.8rem',
  color: 'rgba(255, 255, 255, 0.6)',
  marginRight: theme.spacing(0.5),
}));

interface BuildInfoProps {
  open: boolean;
}

/**
 * Build Info Component
 *
 * Purpose: Displays build information in a compact format suitable for sidebar footer
 * Logic: Reads environment variables and formats them for display
 * Input: open - Whether sidebar is expanded
 * Output: JSX element displaying build information
 *
 * @param open - Whether sidebar is expanded
 * @returns JSX element displaying build information
 */
const BuildInfo: React.FC<BuildInfoProps> = ({ open }) => {
  // Get build information - simplified approach
  const buildDate = import.meta.env.VITE_BUILD_DATE;
  const commitHash = import.meta.env.VITE_VCS_REF;

  // Simple version logic: use git tag if it's a proper version, otherwise use package.json
  const gitVersion = import.meta.env.VITE_GIT_VERSION;
  const packageVersion = import.meta.env.VITE_PACKAGE_VERSION || '1.0.0';

  // Check if git version is a proper version (starts with v and has numbers)
  const isGitVersionValid = gitVersion && gitVersion.match(/^v\d+\.\d+/);
  const version = isGitVersionValid ? gitVersion : packageVersion;

  // Format build date for display
  const formatBuildDate = (dateString: string | undefined) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Unknown';
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return 'Unknown';
    }
  };

  // Create tooltip content with full information
  const tooltipContent = (
    <div>
      <div>
        <strong>Version:</strong> {version || 'Unknown'}
      </div>
      <div>
        <strong>Commit:</strong> {commitHash || 'Unknown'}
      </div>
      <div>
        <strong>Build Date:</strong> {buildDate ? new Date(buildDate).toLocaleString() : 'Unknown'}
      </div>
    </div>
  );

  // If sidebar is collapsed, show just an icon with tooltip
  if (!open) {
    return (
      <BuildInfoContainer>
        <Tooltip title={tooltipContent} placement="right" arrow>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <BuildInfoIcon />
          </Box>
        </Tooltip>
      </BuildInfoContainer>
    );
  }

  // If sidebar is expanded, show compact build info
  return (
    <BuildInfoContainer>
      <Tooltip title={tooltipContent} placement="top" arrow>
        <Box sx={{ cursor: 'help' }}>
          <BuildInfoText variant="caption">
            {version.startsWith('v') ? version : `v${version}`}.
            {commitHash ? commitHash.substring(0, 7) : 'unknown'}
          </BuildInfoText>
          <BuildInfoText variant="caption" sx={{ display: 'block', marginTop: 0.5 }}>
            Updated {formatBuildDate(buildDate)}
          </BuildInfoText>
        </Box>
      </Tooltip>
    </BuildInfoContainer>
  );
};

export default BuildInfo;
